"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowUpRightOutline18, IconCheckOutline18, IconChevronDownOutline18, IconChevronRightOutline18, IconCopyOutline18 } from "nucleo-ui-outline-18";
import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { ChainBadge } from "@/components/ui/workflow-step-row/components/ChainBadge";
import { InlineDetectedValue, InlineJsonValue, isChainIdKey, isEthAddress, looksLikeWalletReference } from "@/components/ui/workflow-step-row/components/InlineAddressLink";
import { useOrganizationWallets } from "@/hooks/useOrganizationWallets";
import { useOrganizations } from "@/hooks/useOrganizations";
import { resolveSlackConversationLabel, type SlackChannel } from "@/app/(v2)/workflows/_components/form-fields/slackChannels";
import { getInlineBadgeClassName } from "@/components/ui/shared-badge-styles";
import { formatExternalUrlForDisplay, getExternalUrl, splitTextByExternalUrls } from "@/lib/utils/externalLinks";
import type { WorkflowNodeExecutionState } from "@/stores/workflowRunsStore";
import React, { createContext, useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";

/**
 * Context to pass execution state and current payload through the component tree for variable resolution
 */
export type ExecutionContext = Record<string, WorkflowNodeExecutionState>;
interface VariableResolutionContext {
  executionState: ExecutionContext | null;
  currentPayload: Record<string, unknown> | null;
  loopVars: Record<string, unknown> | null;
}
const VariableResolutionContextValue = createContext<VariableResolutionContext>({
  executionState: null,
  currentPayload: null,
  loopVars: null
});
export function ExecutionStateProvider(t0) {
  const $ = _c(7);
  const {
    children,
    executionState,
    currentPayload: t1,
    loopVars: t2
  } = t0;
  const currentPayload = t1 === undefined ? null : t1;
  const loopVars = t2 === undefined ? null : t2;
  let t3;
  if ($[0] !== currentPayload || $[1] !== executionState || $[2] !== loopVars) {
    t3 = {
      executionState,
      currentPayload,
      loopVars
    };
    $[0] = currentPayload;
    $[1] = executionState;
    $[2] = loopVars;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== t3) {
    t4 = <VariableResolutionContextValue.Provider value={t3}>{children}</VariableResolutionContextValue.Provider>;
    $[4] = children;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function useVariableResolution() {
  return useContext(VariableResolutionContextValue);
}

/**
 * Resolves a variable path to its actual value from the execution state
 * e.g., "fetch_baseapp_data.result.result.metadata.executionTimeMillis" -> 19222
 */
function resolveVariableValue(variablePath: string, executionState: ExecutionContext | null, currentPayload: Record<string, unknown> | null, loopVars: Record<string, unknown> | null): unknown {
  // Check loop variables first ($item, $index, $for.*)
  if (loopVars) {
    const parts = variablePath.replace(/\[(\d+)\]/g, ".$1").split(".");
    const rootVar = parts[0];

    // Handle $for.nodeId.item/index pattern
    if (rootVar === "$for" && parts.length >= 3) {
      const forKey = `$for.${parts[1]}.${parts[2]}`;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let value: any = loopVars[forKey];
      for (let i = 3; i < parts.length; i++) {
        if (value == null) return undefined;
        value = value[parts[i]];
      }
      if (value !== undefined) return value;
    }

    // Handle $item.* and $index
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = loopVars[rootVar];
    if (value !== undefined) {
      for (let i = 1; i < parts.length; i++) {
        if (value == null) return undefined;
        value = value[parts[i]];
      }
      return value;
    }
  }
  if (!executionState) return undefined;
  let path = variablePath;

  // Handle @ prefix variables - these reference the local payload's data object
  if (path.startsWith("@")) {
    const localVarName = path.slice(1); // Remove @ prefix

    // First try to get from payload's data object
    if (currentPayload && typeof currentPayload.data === "object" && currentPayload.data !== null) {
      const dataObj = currentPayload.data as Record<string, unknown>;
      const localValue = dataObj[localVarName];
      if (localValue !== undefined) {
        // If the local value is itself a template variable, resolve it
        if (typeof localValue === "string" && localValue.startsWith("{{") && localValue.endsWith("}}")) {
          const innerPath = localValue.slice(2, -2).trim();
          return resolveVariableValue(innerPath, executionState, currentPayload, loopVars);
        }
        return localValue;
      }
    }

    // Fallback: try root.result.varName
    path = `root.result.${localVarName}`;
  }

  // Handle array notation like prices[0].prices.usd
  // Convert to dot notation: prices.0.prices.usd
  path = path.replace(/\[(\d+)\]/g, ".$1");
  const parts = path.split(".");
  if (parts.length < 2) return undefined;
  const nodeId = parts[0];
  const nodeState = executionState[nodeId];
  if (!nodeState) return undefined;

  // Navigate through the path starting from the node state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let value: any = nodeState;
  for (let i = 1; i < parts.length; i++) {
    if (value === null || value === undefined) return undefined;
    value = value[parts[i]];
  }
  return value;
}

/**
 * Formats a resolved value for display
 */
function formatResolvedValue(value: unknown): string {
  if (value === undefined) return "Unable to resolve";
  if (value === null) return "null";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return "[Object]";
    }
  }
  return String(value);
}
const ALLOWED_HTML_TAGS = new Set(["B", "STRONG", "I", "EM", "U", "BR", "A", "CODE", "PRE", "P", "SPAN", "DIV", "UL", "OL", "LI"]);
function containsHtml(text: string): boolean {
  return /<[a-zA-Z][^<>]*>/.test(text);
}

// Parse a small allowlisted subset of HTML into React nodes. Used when a
// resolved variable looks like an HTML snippet (e.g. Loop output containing
// <b>, <a href>, <br>). Unknown tags are stripped to their children; only
// http(s) links are kept on <a>.
function renderSafeHtml(html: string, linkClassName?: string): React.ReactNode {
  if (typeof window === "undefined") return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild as HTMLElement | null;
  if (!root) return html;
  const walk = (node: Node, key: string): React.ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const el = node as HTMLElement;
    const tag = el.tagName;
    const children = Array.from(el.childNodes).map((c, i) => walk(c, `${key}-${i}`));
    if (!ALLOWED_HTML_TAGS.has(tag)) {
      return <React.Fragment key={key}>{children}</React.Fragment>;
    }
    if (tag === "BR") return <br key={key} />;
    if (tag === "A") {
      const href = el.getAttribute("href") || "";
      const safe = /^https?:\/\//i.test(href);
      if (!safe) return <React.Fragment key={key}>{children}</React.Fragment>;
      return <a key={key} href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className={linkClassName} title={href}>
          {children}
        </a>;
    }
    const Tag = tag.toLowerCase() as keyof React.JSX.IntrinsicElements;
    return <Tag key={key}>{children}</Tag>;
  };
  return Array.from(root.childNodes).map((c, i) => walk(c, String(i)));
}
function LinkifiedText(t0) {
  const $ = _c(10);
  const {
    text,
    compactUrls: t1,
    linkClassName: t2
  } = t0;
  const compactUrls = t1 === undefined ? false : t1;
  const linkClassName = t2 === undefined ? "underline underline-offset-2 hover:opacity-80" : t2;
  let t3;
  let t4;
  if ($[0] !== compactUrls || $[1] !== linkClassName || $[2] !== text) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const segments = splitTextByExternalUrls(text);
      if (segments.length === 1 && segments[0]?.type === "text") {
        t4 = <>{segments[0].value}</>;
        break bb0;
      }
      let t5;
      if ($[5] !== compactUrls || $[6] !== linkClassName) {
        t5 = (segment, index) => {
          if (segment.type === "text") {
            return <React.Fragment key={`${segment.type}-${index}`}>{segment.value}</React.Fragment>;
          }
          const label = compactUrls ? formatExternalUrlForDisplay(segment.value) : segment.value;
          return <a key={`${segment.type}-${index}`} href={segment.value} target="_blank" rel="noopener noreferrer" onClick={_temp} className={linkClassName} title={segment.value}>{label}</a>;
        };
        $[5] = compactUrls;
        $[6] = linkClassName;
        $[7] = t5;
      } else {
        t5 = $[7];
      }
      t3 = segments.map(t5);
    }
    $[0] = compactUrls;
    $[1] = linkClassName;
    $[2] = text;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[8] !== t3) {
    t5 = <>{t3}</>;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}

/**
 * VariableBadge - Shows a template variable as a badge with resolved value on hover
 * Uses a custom hover tooltip with fixed positioning to work inside modals
 */
function _temp(event) {
  return event.stopPropagation();
}
function VariableBadge(t0) {
  const $ = _c(41);
  const {
    variable
  } = t0;
  const {
    executionState,
    currentPayload,
    loopVars
  } = useVariableResolution();
  const [isVisible, setIsVisible] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      top: 0,
      left: 0,
      placement: "above" as "above" | "below"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [position, setPosition] = useState(t1);
  const [copied, setCopied] = useState(false);
  const badgeRef = React.useRef(null);
  const tooltipRef = React.useRef(null);
  const hideTimeoutRef = React.useRef(null);
  const copyTimeoutRef = React.useRef(null);
  let badgeLabel;
  let formattedValue;
  let fullPath;
  let isHtmlValue;
  let resolvedUrl;
  if ($[1] !== currentPayload || $[2] !== executionState || $[3] !== loopVars || $[4] !== variable) {
    fullPath = variable.slice(2, -2).trim();
    const resolvedValue = resolveVariableValue(fullPath, executionState, currentPayload, loopVars);
    formattedValue = formatResolvedValue(resolvedValue);
    resolvedUrl = typeof resolvedValue === "string" ? getExternalUrl(resolvedValue) : null;
    isHtmlValue = typeof resolvedValue === "string" && containsHtml(resolvedValue);
    const isSimpleValue = resolvedValue !== undefined && (typeof resolvedValue === "string" || typeof resolvedValue === "number" || typeof resolvedValue === "boolean");
    if (resolvedUrl) {
      badgeLabel = formatExternalUrlForDisplay(resolvedUrl);
    } else {
      if (isSimpleValue) {
        badgeLabel = String(resolvedValue);
      } else {
        if (resolvedValue !== undefined && typeof resolvedValue === "object") {
          badgeLabel = Array.isArray(resolvedValue) ? `[${resolvedValue.length} items]` : "{...}";
        } else {
          const parts = fullPath.split(".");
          badgeLabel = parts[parts.length - 1] || fullPath;
        }
      }
    }
    $[1] = currentPayload;
    $[2] = executionState;
    $[3] = loopVars;
    $[4] = variable;
    $[5] = badgeLabel;
    $[6] = formattedValue;
    $[7] = fullPath;
    $[8] = isHtmlValue;
    $[9] = resolvedUrl;
  } else {
    badgeLabel = $[5];
    formattedValue = $[6];
    fullPath = $[7];
    isHtmlValue = $[8];
    resolvedUrl = $[9];
  }
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const cancelHide = t2;
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      cancelHide();
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 150);
    };
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const scheduleHide = t3;
  let t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      cancelHide();
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
    t5 = [];
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== formattedValue) {
    t6 = async e => {
      e.stopPropagation();
      ;
      try {
        await navigator.clipboard.writeText(formattedValue);
        setCopied(true);
        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current);
        }
        copyTimeoutRef.current = setTimeout(() => {
          setCopied(false);
          copyTimeoutRef.current = null;
        }, 1500);
      } catch (t7) {
        const err = t7;
        console.error("Failed to copy to clipboard", err);
      }
    };
    $[14] = formattedValue;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleCopyValue = t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      cancelHide();
      if (badgeRef.current) {
        const rect = badgeRef.current.getBoundingClientRect();
        const preferredPlacement = rect.top < 180 ? "below" : "above";
        setPosition({
          top: preferredPlacement === "above" ? rect.top - 8 : rect.bottom + 8,
          left: rect.left + rect.width / 2,
          placement: preferredPlacement
        });
      }
      setIsVisible(true);
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleBadgeMouseEnter = t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      scheduleHide();
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleBadgeMouseLeave = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      cancelHide();
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleTooltipMouseEnter = t9;
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      scheduleHide();
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleTooltipMouseLeave = t10;
  let t11;
  if ($[20] !== badgeLabel) {
    t11 = <span className="truncate">{badgeLabel}</span>;
    $[20] = badgeLabel;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== resolvedUrl) {
    t12 = resolvedUrl ? <IconArrowUpRightOutline18 className="h-2.5 w-2.5 flex-shrink-0 opacity-60" aria-hidden="true" /> : null;
    $[22] = resolvedUrl;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const badgeContent = t13;
  let t14;
  if ($[27] !== badgeContent || $[28] !== resolvedUrl) {
    t14 = resolvedUrl ? <a ref={element => {
      badgeRef.current = element;
    }} href={resolvedUrl} target="_blank" rel="noopener noreferrer" onClick={_temp2} onMouseEnter={handleBadgeMouseEnter} onMouseLeave={handleBadgeMouseLeave} className={`${"mx-0.5 inline-flex max-w-[14rem] items-center gap-1 rounded-ds-xs bg-blue-100 px-1.5 py-0.5 font-mono text-3xs font-medium text-blue-700 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-900/70"} cursor-pointer`} title={resolvedUrl}>{badgeContent}</a> : <span ref={element_0 => {
      badgeRef.current = element_0;
    }} onMouseEnter={handleBadgeMouseEnter} onMouseLeave={handleBadgeMouseLeave} className={`${"mx-0.5 inline-flex max-w-[14rem] items-center gap-1 rounded-ds-xs bg-blue-100 px-1.5 py-0.5 font-mono text-3xs font-medium text-blue-700 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-900/70"} cursor-default`}>{badgeContent}</span>;
    $[27] = badgeContent;
    $[28] = resolvedUrl;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== copied || $[31] !== formattedValue || $[32] !== fullPath || $[33] !== handleCopyValue || $[34] !== isHtmlValue || $[35] !== isVisible || $[36] !== position) {
    t15 = isVisible && ReactDOM.createPortal(<div ref={tooltipRef} onMouseEnter={handleTooltipMouseEnter} onMouseLeave={handleTooltipMouseLeave} style={{
      position: "fixed",
      top: position.top,
      left: position.left,
      transform: position.placement === "above" ? "translate(-50%, -100%)" : "translate(-50%, 0)",
      zIndex: 99999
    }}>{position.placement === "below" ? null : <div className="h-3" />}<div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 max-w-sm rounded-xl border p-3"><div className="space-y-2"><div className="space-y-0.5"><div className="micro-label">Variable</div><div className="mono-xs text-blue-600 dark:text-blue-400">{fullPath}</div></div><div className="space-y-0.5"><div className="flex items-center justify-between"><div className="micro-label">Resolved Value</div><button onClick={handleCopyValue} className="text-ds-text-disabled hover:bg-ds-surface-hover hover:text-ds-text-subtle dark:hover:bg-ds-neutral-700 rounded-md p-1 transition-colors dark:hover:text-zinc-300" title="Copy value" aria-label="Copy resolved value">{copied ? <IconCheckOutline18 className="h-3 w-3 text-green-500" /> : <IconCopyOutline18 className="h-3 w-3" />}</button></div><div className={`text-ds-text-secondary max-h-64 overflow-y-auto whitespace-pre-wrap ${isHtmlValue ? "text-xs leading-relaxed [&_a]:break-all" : "mono-xs"}`}>{isHtmlValue ? renderSafeHtml(formattedValue, "break-all text-blue-600 underline underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300") : <LinkifiedText text={formattedValue} linkClassName="break-all text-blue-600 underline underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300" />}</div></div></div></div>{position.placement === "below" ? <div className="h-3" /> : null}</div>, document.body);
    $[30] = copied;
    $[31] = formattedValue;
    $[32] = fullPath;
    $[33] = handleCopyValue;
    $[34] = isHtmlValue;
    $[35] = isVisible;
    $[36] = position;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <>{t14}{t15}</>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
}

/**
 * Highlights template variables ({{...}}) in a string as badges with tooltips
 */
function _temp2(event) {
  return event.stopPropagation();
}
function highlightTemplateVariables(text: string): React.ReactNode {
  const parts = text.split(/(\{\{[^}]+\}\})/g);
  if (parts.length === 1) {
    return <LinkifiedText text={text} />;
  }
  return parts.map((part, i) => {
    if (part.startsWith("{{") && part.endsWith("}}")) {
      return <VariableBadge key={i} variable={part} />;
    }
    return <span key={i}>
        <LinkifiedText text={part} />
      </span>;
  });
}

/**
 * Renders a string that may contain template variables. When the entire string
 * is a single template that resolves to an eth address, renders the resolved
 * address with token/wallet badge enhancement alongside the variable badge.
 */
function ResolvedTemplateString(t0) {
  const $ = _c(26);
  const {
    value,
    keyName,
    parentObject,
    wallets
  } = t0;
  const {
    executionState,
    currentPayload,
    loopVars
  } = useVariableResolution();
  if (!executionState && !loopVars) {
    let t1;
    if ($[0] !== value) {
      t1 = highlightTemplateVariables(value);
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <>{t1}</>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== currentPayload || $[5] !== executionState || $[6] !== keyName || $[7] !== loopVars || $[8] !== parentObject || $[9] !== value || $[10] !== wallets) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const singleTemplateMatch = value.match(/^\{\{(.+?)\}\}$/);
      if (singleTemplateMatch) {
        const resolvedValue = resolveVariableValue(singleTemplateMatch[1].trim(), executionState, currentPayload, loopVars);
        if (typeof resolvedValue === "string" && (isEthAddress(resolvedValue) || looksLikeWalletReference(resolvedValue))) {
          let t2;
          if ($[12] !== value) {
            t2 = <VariableBadge variable={value} />;
            $[12] = value;
            $[13] = t2;
          } else {
            t2 = $[13];
          }
          let t3;
          if ($[14] !== keyName || $[15] !== parentObject || $[16] !== resolvedValue || $[17] !== wallets) {
            t3 = <InlineDetectedValue value={resolvedValue} keyName={keyName} parentObject={parentObject} wallets={wallets} variant="json" />;
            $[14] = keyName;
            $[15] = parentObject;
            $[16] = resolvedValue;
            $[17] = wallets;
            $[18] = t3;
          } else {
            t3 = $[18];
          }
          let t4;
          if ($[19] !== t2 || $[20] !== t3) {
            t4 = <span className="inline-flex items-center gap-0.5">{t2}{t3}</span>;
            $[19] = t2;
            $[20] = t3;
            $[21] = t4;
          } else {
            t4 = $[21];
          }
          t1 = t4;
          break bb0;
        }
      }
    }
    $[4] = currentPayload;
    $[5] = executionState;
    $[6] = keyName;
    $[7] = loopVars;
    $[8] = parentObject;
    $[9] = value;
    $[10] = wallets;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  let t2;
  if ($[22] !== value) {
    t2 = highlightTemplateVariables(value);
    $[22] = value;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] !== t2) {
    t3 = <>{t2}</>;
    $[24] = t2;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  return t3;
}

/**
 * InlineChainBadge - Shows a chain ID with icon and name inline in JSON
 */
export function InlineChainBadge(t0) {
  const $ = _c(14);
  const {
    chainId
  } = t0;
  let t1;
  if ($[0] !== chainId) {
    t1 = [chainId];
    $[0] = chainId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: chainInfoMap
  } = useChainInfoMap(t1);
  let t2;
  if ($[2] !== chainId || $[3] !== chainInfoMap) {
    t2 = chainInfoMap.get(chainId);
    $[2] = chainId;
    $[3] = chainInfoMap;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const chain = t2;
  if (!chain) {
    let t3;
    if ($[5] !== chainId) {
      t3 = <span className="text-blue-600 dark:text-blue-400">{chainId}</span>;
      $[5] = chainId;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t3;
  if ($[7] !== chainId) {
    t3 = <span className="text-blue-600 dark:text-blue-400">{chainId}</span>;
    $[7] = chainId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== chain) {
    t4 = <ChainBadge chain={chain} size="sm" variant="json" className="ml-1 scale-90" />;
    $[9] = chain;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <span className="inline-flex items-center gap-1">{t3}{t4}</span>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
export interface EnhancedJsonViewProps {
  value: unknown;
  defaultExpanded?: boolean;
  expandAll?: boolean;
  keyName?: string;
  /** Parent object context, used to extract chainId for token lookups */
  parentObject?: Record<string, unknown>;
  /** Hide outer braces/brackets visually (still included when copying) */
  hideOuterBraces?: boolean;
  organizationWallets?: ReturnType<typeof useOrganizationWallets>["wallets"];
  /** Slack channels map for resolving channel IDs to names */
  slackChannelsMap?: Map<string, SlackChannel>;
}

/**
 * EnhancedJsonView - Renders a JSON value with syntax highlighting and collapsible sections
 * Enhanced to show addresses with ENS avatars and chain IDs with badges
 */
export function EnhancedJsonView(t0) {
  const $ = _c(102);
  const {
    value,
    defaultExpanded: t1,
    expandAll: t2,
    keyName,
    parentObject,
    hideOuterBraces: t3,
    organizationWallets,
    slackChannelsMap
  } = t0;
  const defaultExpanded = t1 === undefined ? true : t1;
  const expandAll = t2 === undefined ? false : t2;
  const hideOuterBraces = t3 === undefined ? false : t3;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const isOpen = expandAll || isExpanded;
  if (value === null) {
    let t4;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <span className="text-blue-600 dark:text-blue-400">null</span>;
      $[0] = t4;
    } else {
      t4 = $[0];
    }
    return t4;
  }
  if (typeof value === "boolean") {
    const t4 = String(value);
    let t5;
    if ($[1] !== t4) {
      t5 = <span className="text-purple-600 dark:text-purple-400">{t4}</span>;
      $[1] = t4;
      $[2] = t5;
    } else {
      t5 = $[2];
    }
    return t5;
  }
  if (typeof value === "number") {
    if (keyName && isChainIdKey(keyName)) {
      let t4;
      if ($[3] !== value) {
        t4 = <InlineChainBadge chainId={value} />;
        $[3] = value;
        $[4] = t4;
      } else {
        t4 = $[4];
      }
      return t4;
    }
    let t4;
    if ($[5] !== value) {
      t4 = <span className="text-blue-600 dark:text-blue-400">{value}</span>;
      $[5] = value;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  if (typeof value === "string") {
    if (slackChannelsMap && (keyName === "channel" || keyName === "conversation")) {
      let t4;
      if ($[7] !== slackChannelsMap || $[8] !== value) {
        t4 = resolveSlackConversationLabel(value, slackChannelsMap);
        $[7] = slackChannelsMap;
        $[8] = value;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      const resolved = t4;
      if (resolved && resolved !== value) {
        const t5 = `"${value}"`;
        let t6;
        if ($[10] !== t5) {
          t6 = <span className="text-green-700 dark:text-green-400">{t5}</span>;
          $[10] = t5;
          $[11] = t6;
        } else {
          t6 = $[11];
        }
        let t7;
        if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = getInlineBadgeClassName({
            variant: "json"
          });
          $[12] = t7;
        } else {
          t7 = $[12];
        }
        let t8;
        if ($[13] !== resolved || $[14] !== t7) {
          t8 = <span className={t7}>{resolved}</span>;
          $[13] = resolved;
          $[14] = t7;
          $[15] = t8;
        } else {
          t8 = $[15];
        }
        let t9;
        if ($[16] !== t6 || $[17] !== t8) {
          t9 = <span className="inline-flex items-center gap-1">{t6}{t8}</span>;
          $[16] = t6;
          $[17] = t8;
          $[18] = t9;
        } else {
          t9 = $[18];
        }
        return t9;
      }
    }
    const isAddressLikeValue = looksLikeWalletReference(value) || isEthAddress(value);
    const shouldUseInlineValue = isAddressLikeValue;
    if (shouldUseInlineValue) {
      let t4;
      if ($[19] !== keyName || $[20] !== organizationWallets || $[21] !== parentObject || $[22] !== value) {
        t4 = <InlineJsonValue value={value} keyName={keyName} parentObject={parentObject} wallets={organizationWallets} variant="json" />;
        $[19] = keyName;
        $[20] = organizationWallets;
        $[21] = parentObject;
        $[22] = value;
        $[23] = t4;
      } else {
        t4 = $[23];
      }
      return t4;
    }
    let t4;
    if ($[24] !== keyName || $[25] !== organizationWallets || $[26] !== parentObject || $[27] !== value) {
      t4 = <span className="whitespace-pre-wrap break-words text-green-700 dark:text-green-400">{"\""}<ResolvedTemplateString value={value} keyName={keyName} parentObject={parentObject} wallets={organizationWallets} />{"\""}</span>;
      $[24] = keyName;
      $[25] = organizationWallets;
      $[26] = parentObject;
      $[27] = value;
      $[28] = t4;
    } else {
      t4 = $[28];
    }
    return t4;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      let t4;
      if ($[29] !== hideOuterBraces) {
        t4 = hideOuterBraces ? null : <span className="text-ds-text-muted">[]</span>;
        $[29] = hideOuterBraces;
        $[30] = t4;
      } else {
        t4 = $[30];
      }
      return t4;
    }
    if (hideOuterBraces) {
      let t4;
      if ($[31] !== defaultExpanded || $[32] !== expandAll || $[33] !== keyName || $[34] !== organizationWallets || $[35] !== parentObject || $[36] !== slackChannelsMap || $[37] !== value) {
        let t5;
        if ($[39] !== defaultExpanded || $[40] !== expandAll || $[41] !== keyName || $[42] !== organizationWallets || $[43] !== parentObject || $[44] !== slackChannelsMap || $[45] !== value.length) {
          t5 = (item, i) => <span key={i} className="block"><EnhancedJsonView value={item} defaultExpanded={defaultExpanded} expandAll={expandAll} keyName={keyName} parentObject={parentObject} organizationWallets={organizationWallets} slackChannelsMap={slackChannelsMap} />{i < value.length - 1 && <span className="text-ds-text-muted">,</span>}</span>;
          $[39] = defaultExpanded;
          $[40] = expandAll;
          $[41] = keyName;
          $[42] = organizationWallets;
          $[43] = parentObject;
          $[44] = slackChannelsMap;
          $[45] = value.length;
          $[46] = t5;
        } else {
          t5 = $[46];
        }
        t4 = value.map(t5);
        $[31] = defaultExpanded;
        $[32] = expandAll;
        $[33] = keyName;
        $[34] = organizationWallets;
        $[35] = parentObject;
        $[36] = slackChannelsMap;
        $[37] = value;
        $[38] = t4;
      } else {
        t4 = $[38];
      }
      let t5;
      if ($[47] !== t4) {
        t5 = <span className="inline">{t4}</span>;
        $[47] = t4;
        $[48] = t5;
      } else {
        t5 = $[48];
      }
      return t5;
    }
    let t4;
    if ($[49] !== expandAll || $[50] !== isExpanded) {
      t4 = () => !expandAll && setIsExpanded(!isExpanded);
      $[49] = expandAll;
      $[50] = isExpanded;
      $[51] = t4;
    } else {
      t4 = $[51];
    }
    let t5;
    if ($[52] !== isOpen) {
      t5 = isOpen ? <IconChevronDownOutline18 className="h-3 w-3" /> : <IconChevronRightOutline18 className="h-3 w-3" />;
      $[52] = isOpen;
      $[53] = t5;
    } else {
      t5 = $[53];
    }
    let t6;
    if ($[54] !== t4 || $[55] !== t5) {
      t6 = <button onClick={t4} className="text-ds-text-muted hover:text-ds-text-secondary inline-flex items-center dark:hover:text-zinc-300">{t5}</button>;
      $[54] = t4;
      $[55] = t5;
      $[56] = t6;
    } else {
      t6 = $[56];
    }
    let t7;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <span className="text-ds-text-muted">[</span>;
      $[57] = t7;
    } else {
      t7 = $[57];
    }
    let t8;
    if ($[58] !== expandAll || $[59] !== isOpen || $[60] !== keyName || $[61] !== organizationWallets || $[62] !== parentObject || $[63] !== slackChannelsMap || $[64] !== value) {
      t8 = isOpen ? <span className="block pl-4">{value.map((item_0, i_0) => <span key={i_0} className="block"><EnhancedJsonView value={item_0} defaultExpanded={expandAll} expandAll={expandAll} keyName={keyName} parentObject={parentObject} organizationWallets={organizationWallets} slackChannelsMap={slackChannelsMap} />{i_0 < value.length - 1 && <span className="text-ds-text-muted">,</span>}</span>)}</span> : <span className="text-ds-text-disabled">...</span>;
      $[58] = expandAll;
      $[59] = isOpen;
      $[60] = keyName;
      $[61] = organizationWallets;
      $[62] = parentObject;
      $[63] = slackChannelsMap;
      $[64] = value;
      $[65] = t8;
    } else {
      t8 = $[65];
    }
    let t9;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <span className="text-ds-text-muted">]</span>;
      $[66] = t9;
    } else {
      t9 = $[66];
    }
    let t10;
    if ($[67] !== t6 || $[68] !== t8) {
      t10 = <span className="inline">{t6}{t7}{t8}{t9}</span>;
      $[67] = t6;
      $[68] = t8;
      $[69] = t10;
    } else {
      t10 = $[69];
    }
    return t10;
  }
  if (typeof value === "object") {
    let t4;
    let t5;
    let t6;
    let t7;
    let t8;
    if ($[70] !== defaultExpanded || $[71] !== expandAll || $[72] !== hideOuterBraces || $[73] !== isExpanded || $[74] !== isOpen || $[75] !== organizationWallets || $[76] !== slackChannelsMap || $[77] !== value) {
      t8 = Symbol.for("react.early_return_sentinel");
      bb0: {
        const entries = Object.entries(value as Record<string, unknown>);
        if (entries.length === 0) {
          let t9;
          if ($[83] !== hideOuterBraces) {
            t9 = hideOuterBraces ? null : <span className="text-ds-text-muted">{"{}"}</span>;
            $[83] = hideOuterBraces;
            $[84] = t9;
          } else {
            t9 = $[84];
          }
          t8 = t9;
          break bb0;
        }
        if (hideOuterBraces) {
          t8 = <span className="inline">{entries.map((t9, i_1) => {
              const [key, val] = t9;
              return <span key={key} className="block"><span className="text-rose-600 dark:text-rose-400">{"\""}{highlightTemplateVariables(key)}{"\""}</span><span className="text-ds-text-muted">: </span><EnhancedJsonView value={val} defaultExpanded={defaultExpanded} expandAll={expandAll} keyName={key} parentObject={value as Record<string, unknown>} organizationWallets={organizationWallets} slackChannelsMap={slackChannelsMap} />{i_1 < entries.length - 1 && <span className="text-ds-text-muted">,</span>}</span>;
            })}</span>;
          break bb0;
        }
        t4 = "inline";
        let t9;
        if ($[85] !== expandAll || $[86] !== isExpanded) {
          t9 = () => !expandAll && setIsExpanded(!isExpanded);
          $[85] = expandAll;
          $[86] = isExpanded;
          $[87] = t9;
        } else {
          t9 = $[87];
        }
        let t10;
        if ($[88] !== isOpen) {
          t10 = isOpen ? <IconChevronDownOutline18 className="h-3 w-3" /> : <IconChevronRightOutline18 className="h-3 w-3" />;
          $[88] = isOpen;
          $[89] = t10;
        } else {
          t10 = $[89];
        }
        if ($[90] !== t10 || $[91] !== t9) {
          t5 = <button onClick={t9} className="text-ds-text-muted hover:text-ds-text-secondary inline-flex items-center dark:hover:text-zinc-300">{t10}</button>;
          $[90] = t10;
          $[91] = t9;
          $[92] = t5;
        } else {
          t5 = $[92];
        }
        if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <span className="text-ds-text-muted">{"{"}</span>;
          $[93] = t6;
        } else {
          t6 = $[93];
        }
        t7 = isOpen ? <span className="block pl-4">{entries.map((t11, i_2) => {
            const [key_0, val_0] = t11;
            return <span key={key_0} className="block"><span className="text-rose-600 dark:text-rose-400">{"\""}{highlightTemplateVariables(key_0)}{"\""}</span><span className="text-ds-text-muted">: </span><EnhancedJsonView value={val_0} defaultExpanded={expandAll} expandAll={expandAll} keyName={key_0} parentObject={value as Record<string, unknown>} organizationWallets={organizationWallets} slackChannelsMap={slackChannelsMap} />{i_2 < entries.length - 1 && <span className="text-ds-text-muted">,</span>}</span>;
          })}</span> : <span className="text-ds-text-disabled">...</span>;
      }
      $[70] = defaultExpanded;
      $[71] = expandAll;
      $[72] = hideOuterBraces;
      $[73] = isExpanded;
      $[74] = isOpen;
      $[75] = organizationWallets;
      $[76] = slackChannelsMap;
      $[77] = value;
      $[78] = t4;
      $[79] = t5;
      $[80] = t6;
      $[81] = t7;
      $[82] = t8;
    } else {
      t4 = $[78];
      t5 = $[79];
      t6 = $[80];
      t7 = $[81];
      t8 = $[82];
    }
    if (t8 !== Symbol.for("react.early_return_sentinel")) {
      return t8;
    }
    let t9;
    if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <span className="text-ds-text-muted">{"}"}</span>;
      $[94] = t9;
    } else {
      t9 = $[94];
    }
    let t10;
    if ($[95] !== t4 || $[96] !== t5 || $[97] !== t6 || $[98] !== t7) {
      t10 = <span className={t4}>{t5}{t6}{t7}{t9}</span>;
      $[95] = t4;
      $[96] = t5;
      $[97] = t6;
      $[98] = t7;
      $[99] = t10;
    } else {
      t10 = $[99];
    }
    return t10;
  }
  const t4 = String(value);
  let t5;
  if ($[100] !== t4) {
    t5 = <span className="text-ds-text-muted">{t4}</span>;
    $[100] = t4;
    $[101] = t5;
  } else {
    t5 = $[101];
  }
  return t5;
}

/**
 * Checks if a value has nested objects/arrays that would benefit from expand all.
 * Performs a shallow check - returns true if any direct child is an object or array.
 */
export function hasNestedContent(value: unknown): boolean {
  if (value === null || typeof value !== "object") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0 && value.some(item => typeof item === "object" && item !== null);
  }
  const entries = Object.entries(value);
  return entries.some(([, val]) => typeof val === "object" && val !== null);
}
export interface EnhancedJsonViewWithCopyProps {
  value: unknown;
  defaultExpanded?: boolean;
  expandAll?: boolean;
  className?: string;
  /** Execution state from workflow run, used to resolve variable values */
  executionState?: ExecutionContext | null;
  /** Loop variables from for-each iteration context (e.g., $item, $index) */
  loopVars?: Record<string, unknown> | null;
  /** Show expand all link */
  showExpandAll?: boolean;
  /** Slack channels map for resolving channel IDs to names */
  slackChannelsMap?: Map<string, SlackChannel>;
}

/**
 * EnhancedJsonViewWithCopy - Wrapper that adds a copy button to EnhancedJsonView
 */
export function EnhancedJsonViewWithCopy(t0) {
  const $ = _c(32);
  const {
    value,
    defaultExpanded: t1,
    expandAll: t2,
    className: t3,
    executionState: t4,
    loopVars: t5,
    showExpandAll: t6,
    slackChannelsMap
  } = t0;
  const defaultExpanded = t1 === undefined ? true : t1;
  const expandAll = t2 === undefined ? false : t2;
  const className = t3 === undefined ? "" : t3;
  const executionState = t4 === undefined ? null : t4;
  const loopVars = t5 === undefined ? null : t5;
  const showExpandAll = t6 === undefined ? true : t6;
  const [copied, setCopied] = useState(false);
  const [forceExpandAll, setForceExpandAll] = useState(expandAll);
  const {
    selectedOrgId
  } = useOrganizations();
  const {
    wallets: organizationWallets
  } = useOrganizationWallets(selectedOrgId ?? undefined);
  const effectiveExpandAll = expandAll || forceExpandAll;
  let t7;
  if ($[0] !== value) {
    t7 = async () => {
      ;
      try {
        await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (t8) {
        const err = t8;
        console.error("Failed to copy to clipboard", err);
      }
    };
    $[0] = value;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const handleCopy = t7;
  const currentPayload = typeof value === "object" && value !== null ? value as Record<string, unknown> : null;
  let t8;
  if ($[2] !== effectiveExpandAll || $[3] !== showExpandAll || $[4] !== value) {
    t8 = showExpandAll && !effectiveExpandAll && hasNestedContent(value);
    $[2] = effectiveExpandAll;
    $[3] = showExpandAll;
    $[4] = value;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const shouldShowExpandAll = t8;
  const t9 = `relative ${className}`;
  let t10;
  if ($[6] !== shouldShowExpandAll) {
    t10 = shouldShowExpandAll && <button onClick={() => setForceExpandAll(true)} className="text-3xs font-medium text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">Expand all</button>;
    $[6] = shouldShowExpandAll;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== copied) {
    t11 = copied ? <IconCheckOutline18 className="h-3.5 w-3.5 text-green-500" /> : <IconCopyOutline18 className="h-3.5 w-3.5" />;
    $[8] = copied;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== handleCopy || $[11] !== t11) {
    t12 = <button onClick={handleCopy} className="text-ds-text-disabled transition-colors hover:text-blue-600 dark:hover:text-blue-400" title="Copy JSON">{t11}</button>;
    $[10] = handleCopy;
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t10 || $[14] !== t12) {
    t13 = <div className="absolute right-0 top-0 flex items-center gap-2">{t10}{t12}</div>;
    $[13] = t10;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const t14 = effectiveExpandAll ? "expanded" : "collapsed";
  let t15;
  if ($[16] !== defaultExpanded || $[17] !== effectiveExpandAll || $[18] !== organizationWallets || $[19] !== slackChannelsMap || $[20] !== t14 || $[21] !== value) {
    t15 = <EnhancedJsonView key={t14} value={value} defaultExpanded={defaultExpanded} expandAll={effectiveExpandAll} hideOuterBraces={true} organizationWallets={organizationWallets} slackChannelsMap={slackChannelsMap} />;
    $[16] = defaultExpanded;
    $[17] = effectiveExpandAll;
    $[18] = organizationWallets;
    $[19] = slackChannelsMap;
    $[20] = t14;
    $[21] = value;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t13 || $[24] !== t15 || $[25] !== t9) {
    t16 = <div className={t9}>{t13}{t15}</div>;
    $[23] = t13;
    $[24] = t15;
    $[25] = t9;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const content = t16;
  if (executionState || loopVars) {
    let t17;
    if ($[27] !== content || $[28] !== currentPayload || $[29] !== executionState || $[30] !== loopVars) {
      t17 = <ExecutionStateProvider executionState={executionState} currentPayload={currentPayload} loopVars={loopVars}>{content}</ExecutionStateProvider>;
      $[27] = content;
      $[28] = currentPayload;
      $[29] = executionState;
      $[30] = loopVars;
      $[31] = t17;
    } else {
      t17 = $[31];
    }
    return t17;
  }
  return content;
}

/**
 * Parses an error string to extract text message and JSON body parts
 */
export function parseErrorMessage(error: string): {
  message: string;
  jsonBody: Record<string, unknown> | null;
} {
  // Try to find JSON in the error string - look for patterns like "body: {...}" or standalone JSON
  const bodyMatch = error.match(/body:\s*(\{[\s\S]*\})/);
  if (bodyMatch) {
    try {
      const jsonBody = JSON.parse(bodyMatch[1]) as Record<string, unknown>;
      const message = error.substring(0, bodyMatch.index).trim().replace(/,?\s*$/, "");
      return {
        message,
        jsonBody
      };
    } catch {
      // JSON parse failed, return as plain error
    }
  }

  // Try to find standalone JSON object at the end of the string
  const jsonMatch = error.match(/(\{[\s\S]*\})$/);
  if (jsonMatch) {
    try {
      const jsonBody = JSON.parse(jsonMatch[1]) as Record<string, unknown>;
      const message = error.substring(0, jsonMatch.index).trim().replace(/,?\s*$/, "");
      return {
        message,
        jsonBody
      };
    } catch {
      // JSON parse failed, return as plain error
    }
  }
  return {
    message: error,
    jsonBody: null
  };
}